import { Box, Tag, TagLabel } from '@chakra-ui/react';
import { ReactComponent as MissingAltTagIcon } from 'assets/icons/a11ymenu_missing_alt.svg';
import { ReactComponent as AltTagIcon } from 'assets/icons/a11ymenu_status_ok_transparent.svg';
import { TAG_ALT_TEXT_WIDGETS_MAP } from 'constants/altTextTagWidgets';
import { IGNORE_TARGET_CLASS } from 'constants/bounding-box';
import { ALT_TEXT_MODAL_OPENED } from 'libs/third-party/Mixpanel';
import { AltTextModalTrigger } from 'modules/Editor/components/AltTextModal/AltTextModalTrigger';
import { useAppSelector } from 'modules/Editor/store';
import { selectShowTagOverlay } from 'modules/Editor/store/pageSelector';
import { selectWidgetActionState } from 'modules/Editor/store/widgetSelector';
import { memo } from 'react';
import { WidgetId } from 'types/idTypes';
import { AccessibleElement, Widget } from 'types/widget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

import { useEditor } from './useEditor';
import { useWidget } from './useWidget';

// Widgets that should have alt text tag overlay when selected
export const hasAltTextTag = (widgetId: WidgetId) => TAG_ALT_TEXT_WIDGETS_MAP.has(getWidgetTypeFromId(widgetId));

export const AltTextTag = memo(() => {
  const { widgetId, widthPx, heightPx, topPx, leftPx, rotateDeg, isDecorative, isWidgetSelected } = useWidget<
    AccessibleElement & Widget
  >();
  const { isCropView } = useEditor();
  const showTagOverlay = useAppSelector(selectShowTagOverlay);

  if (!isWidgetSelected || showTagOverlay || isCropView) return null;
  if (isDecorative) return null;

  const style = {
    width: `${widthPx}px`,
    height: `${heightPx}px`,
    top: `${topPx}px`,
    left: `${leftPx}px`,
    transform: `rotate(${rotateDeg}deg)`,
    //should be on top of other widgets
    zIndex: 99999,
  } as React.CSSProperties;

  return (
    <Box style={style} pointerEvents='none' position='absolute'>
      <AltTextModalTrigger trigger={<ModalTrigger />} widgetId={widgetId} mpFrom={ALT_TEXT_MODAL_OPENED.from.AltTag} />
    </Box>
  );
});

interface ModalTriggerProps {
  onClick?: () => void;
}

const iconProps = { height: '1em', width: '1em' };

const ModalTrigger = memo(({ onClick }: ModalTriggerProps) => {
  const { altText } = useWidget<AccessibleElement & Widget>();

  const widgetActionState = useAppSelector(selectWidgetActionState);
  const visibility = widgetActionState ? 'hidden' : undefined;

  const isMissingAltText = !altText;
  const tagLabel = 'ALT';

  return (
    <Tag
      className={IGNORE_TARGET_CLASS}
      visibility={visibility}
      size={'sm'}
      variant={isMissingAltText ? 'missing-alt' : 'nonText-tag'}
      pointerEvents={'auto'}
      maxH={'100%'}
      onClick={onClick}
      data-testid='altTextTag'
      position='absolute'
      top={`2px`}
      left={`2px`}
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      gap='0.25rem'
      as='button'
    >
      {isMissingAltText ? <MissingAltTagIcon {...iconProps} /> : <AltTagIcon {...iconProps} color='white' />}
      <TagLabel>{tagLabel}</TagLabel>
    </Tag>
  );
});

AltTextTag.displayName = 'AltTextTag';
ModalTrigger.displayName = 'ModalTrigger';
